import React, {useLayoutEffect, useMemo, useRef, useState} from 'react';
import {wordCloudPaletteColors} from '../../../../incase-domain/elements/question/wordCloudPalettes';

const MIN_FONT_SIZE = 30;
const MAX_FONT_SIZE = 56;
const EVEN_FONT_SIZE = 32;

export type WordCloudWord = {
    text: string;
    count: number;
};

type PlacedWord = WordCloudWord & {
    x: number;
    y: number;
    fontSize: number;
    color: string;
};

type Box = {x: number; y: number; width: number; height: number};

interface WordCloudProps {
    words: WordCloudWord[];
    palette?: string;
    onRemove?: (word: string) => void;
}

function measureWord(text: string, fontSize: number): {width: number; height: number} {
    return {
        width: Math.ceil(text.length * fontSize * 0.6) + 4,
        height: Math.ceil(fontSize * 1.15),
    };
}

function fontSizeForCount(count: number, minCount: number, maxCount: number): number {
    if (maxCount <= minCount) {
        return EVEN_FONT_SIZE;
    }

    const ratio = (count - minCount) / (maxCount - minCount);
    return Math.round(MIN_FONT_SIZE + ratio * (MAX_FONT_SIZE - MIN_FONT_SIZE));
}

function boxInsideReport(text: string, fontSize: number, maxWidth: number, maxHeight: number): {width: number; height: number} {
    const measured = measureWord(text, fontSize);
    return {
        width: Math.max(1, Math.min(measured.width, maxWidth)),
        height: Math.max(1, Math.min(measured.height, maxHeight)),
    };
}

function overlaps(a: Box, b: Box): boolean {
    return !(a.x + a.width <= b.x || b.x + b.width <= a.x || a.y + a.height <= b.y || b.y + b.height <= a.y);
}

function findOpenSpot(
    containerWidth: number,
    containerHeight: number,
    boxWidth: number,
    boxHeight: number,
    occupied: Box[]
): {x: number; y: number} | null {
    const originX = (containerWidth - boxWidth) / 2;
    const originY = (containerHeight - boxHeight) / 2;
    const padding = 8;

    for (let step = 0; step < 500; step += 1) {
        const angle = step * 0.45;
        const radius = step * 2.2;
        const x = Math.round(originX + Math.cos(angle) * radius);
        const y = Math.round(originY + Math.sin(angle) * radius * 0.72);
        if (x >= 0 && y >= 0 && x + boxWidth <= containerWidth && y + boxHeight <= containerHeight) {
            const candidate = {x, y, width: boxWidth + padding, height: boxHeight + padding};
            const blocked = occupied.some((box) =>
                overlaps(candidate, {x: box.x, y: box.y, width: box.width + padding, height: box.height + padding})
            );
            if (!blocked) {
                return {x, y};
            }
        }
    }

    return null;
}

export function layoutWordCloud(
    words: WordCloudWord[],
    width: number,
    height: number,
    colors: readonly string[] = wordCloudPaletteColors()
): PlacedWord[] {
    if (width < 8 || height < 8) {
        return [];
    }

    const visible = words.filter((word) => word.text.trim() && word.count > 0);
    if (!visible.length) {
        return [];
    }

    const counts = visible.map((word) => word.count);
    const minCount = Math.min(...counts);
    const maxCount = Math.max(...counts);
    const colorByText = new Map(
        [...visible].sort((a, b) => a.text.localeCompare(b.text)).map((word, index) => [word.text, colors[index % colors.length]])
    );
    const sorted = [...visible].sort((a, b) => b.count - a.count || a.text.localeCompare(b.text));
    const occupied: Box[] = [];
    const placed: PlacedWord[] = [];

    sorted.forEach((word) => {
        const fontSize = fontSizeForCount(word.count, minCount, maxCount);
        const box = boxInsideReport(word.text, fontSize, width, height);
        const spot = findOpenSpot(width, height, box.width, box.height, occupied);
        if (!spot) {
            return;
        }

        occupied.push({x: spot.x, y: spot.y, width: box.width, height: box.height});
        placed.push({
            ...word,
            x: spot.x,
            y: spot.y,
            fontSize,
            color: colorByText.get(word.text) || colors[0],
        });
    });

    return placed;
}

function WordCloud({words, onRemove, palette}: WordCloudProps): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState({width: 0, height: 0});

    useLayoutEffect(() => {
        const node = containerRef.current;
        if (!node) {
            return undefined;
        }

        const update = (): void => {
            setSize((prev) => {
                if (prev.width === node.clientWidth && prev.height === node.clientHeight) {
                    return prev;
                }
                return {width: node.clientWidth, height: node.clientHeight};
            });
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const colors = wordCloudPaletteColors(palette);
    const placed = useMemo(() => layoutWordCloud(words, size.width, size.height, colors), [words, size.width, size.height, colors]);

    return (
        <div ref={containerRef} className="c-wordCloud">
            {placed.map((word, index) => {
                const maxWidth = Math.max(0, size.width - word.x);
                const style = {left: word.x, top: word.y, fontSize: word.fontSize, color: word.color, maxWidth};
                const title = measureWord(word.text, word.fontSize).width > maxWidth ? word.text : undefined;
                if (!onRemove) {
                    return (
                        <span key={`${word.text}-${index}`} className="c-wordCloud__word" style={style} title={title}>
                            {word.text}
                        </span>
                    );
                }

                return (
                    <button
                        key={`${word.text}-${index}`}
                        type="button"
                        className="c-wordCloud__word c-wordCloud__word--removable"
                        style={style}
                        title={title}
                        onClick={(event) => {
                            event.stopPropagation();
                            onRemove(word.text);
                        }}
                    >
                        <span className="c-wordCloud__label">{word.text}</span>
                    </button>
                );
            })}
        </div>
    );
}

export default WordCloud;
